import * as React from 'react';

import {
  DataGrid,
  DataGridSelection,
  FloatingToolbar,
  FontSemiBold,
  ListScreenLayout,
  Pagination,
} from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';

import { PurchaseOrderRecord } from '../../_graphql/queryPurchaseOrderList';
import * as state from '../../_state/purchaseOrderList.state';
import { customerPortalRoutes } from '../../routes';
import { purchaseOrderDocuments } from '../_shared/availableDocuments';
import {
  DownloadDocumentsButton,
  FetchDocumentsPopover,
  PrintDocumentsButton,
} from '../_shared/FetchDocuments';

import { usePurchaseOrderListColumns } from './hooks/usePurchaseOrderListColumns';
import { PurchaseOrderListFilterBar } from './PurchaseOrderListFilterBar';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';
import { useMultiRef } from '~/shared/hooks/useMultiRef';

//
// ⚛️ ⚛️ ⚛️ PURCHASE ORDER LIST VIEW ⚛️ ⚛️ ⚛️
//
export const PurchaseOrderListView = () => {
  const [, navigate] = useModuleLocation();

  const menuRefs = useMultiRef<HTMLButtonElement>();

  const [selection, setSelection] = React.useState<DataGridSelection>(
    new Set(),
  );
  const [purchaseOrderIdForPrint, setPurchaseOrderIdForPrint] =
    React.useState<string>();
  const [purchaseOrderIdForDownload, setPurchaseOrderIdForDownload] =
    React.useState<string>();

  const [limit = 50, setLimit] = useAtom(state.$limit);
  const [offset, setOffset] = useAtom(state.$offset);
  const {
    data: results,
    isLoading,
    isError,
    error,
  } = useAtomValue(state.$purchaseOrders);

  const handleCellClick = React.useCallback(
    ({ row: purchaseOrder }: { row: PurchaseOrderRecord }) => {
      navigate(customerPortalRoutes.purchaseOrderDetails, {
        params: { purchaseOrderId: purchaseOrder.id },
      });
    },
    [navigate],
  );

  const columns = usePurchaseOrderListColumns({
    onCellClick: handleCellClick,
    navigate: navigate as (
      route: string,
      options?: { params?: Record<string, string> },
    ) => void,
    menuRefs,
    onPrint: setPurchaseOrderIdForPrint,
    onDownload: setPurchaseOrderIdForDownload,
  });

  const purchaseOrders = React.useMemo(
    () => results?.purchaseOrders ?? [],
    [results],
  );
  const total = React.useMemo(() => results?.total ?? 0, [results]);
  const selectedPurchaseOrders = React.useMemo(
    () =>
      purchaseOrders.filter(
        (it) => selection === 'all' || selection.has(it.id),
      ),
    [purchaseOrders, selection],
  );
  const selectedRowsCount = React.useMemo(
    () => (selection === 'all' ? purchaseOrders.length : selection.size),
    [selection, purchaseOrders],
  );

  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );

  return (
    <>
      <ListScreenLayout
        className="@PurchaseOrderListView"
        filters={<PurchaseOrderListFilterBar />}
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<PurchaseOrderRecord>
          columns={columns}
          dataSource={purchaseOrders}
          fullWidth
          rowKey="id"
          selectionMode="multiple"
          isLoading={isLoading}
          selectedKeys={selection}
          onSelectionChange={setSelection}
          hasError={!!isError}
          errorPanelProps={
            error
              ? { description: error.message, centered: true }
              : isError
                ? {
                    description:
                      'Failed to load purchase orders. Please try again.',
                    centered: true,
                  }
                : undefined
          }
          emptyPanelProps={{
            title: "It's empty here",
            description: 'No purchase orders found for the selected filters',
          }}
        />
      </ListScreenLayout>

      <FloatingToolbar visible={selectedRowsCount > 0}>
        <div className="flex items-center gap-2">
          <div className="flex min-w-[210px] items-center justify-end gap-1.5 px-3 text-sm text-neutral/80">
            <FontSemiBold>{selectedRowsCount}</FontSemiBold>
            {selectedRowsCount === 1
              ? 'purchase order'
              : 'purchase orders'}{' '}
            selected
          </div>
          <PrintDocumentsButton
            originKey="purchase-order-list"
            ids={selectedPurchaseOrders.map((it) => it.id)}
            documents={purchaseOrderDocuments}
          />
          <DownloadDocumentsButton
            originKey="purchase-order-list"
            compressedFileName="purchase-orders"
            ids={selectedPurchaseOrders.map((it) => it.id)}
            documents={purchaseOrderDocuments}
          />
        </div>
      </FloatingToolbar>

      {purchaseOrderIdForPrint && (
        <FetchDocumentsPopover
          isOpen
          type="print"
          documents={purchaseOrderDocuments}
          originKey="purchase-order-list-row"
          triggerRef={menuRefs(purchaseOrderIdForPrint)}
          ids={[purchaseOrderIdForPrint]}
          onOpenChange={() => setPurchaseOrderIdForPrint(undefined)}
        />
      )}
      {purchaseOrderIdForDownload && (
        <FetchDocumentsPopover
          isOpen
          type="download"
          documents={purchaseOrderDocuments}
          originKey="purchase-order-list-row"
          triggerRef={menuRefs(purchaseOrderIdForDownload)}
          ids={[purchaseOrderIdForDownload]}
          onOpenChange={() => setPurchaseOrderIdForDownload(undefined)}
        />
      )}
    </>
  );
};
